import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, MessageSquare, Mail, Send, CheckCircle2, AlertCircle, PhoneIncoming, Copy, RefreshCw, Loader2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import api from "../lib/api";
import { initials } from "../lib/utils-format";

function Section({ title, children, right }) {
  return (
    <div className="rounded-md border border-border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-semibold">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function LivePill({ live }) {
  return live ? (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-400" data-testid="comm-live-pill">
      <CheckCircle2 className="h-3.5 w-3.5" /> Live
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-400" data-testid="comm-notlive-pill">
      <AlertCircle className="h-3.5 w-3.5" /> Not configured
    </span>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground/70">{hint}</p>}
    </div>
  );
}

function CommunicationsSettings() {
  const [data, setData] = useState(null);
  const [tw, setTw] = useState({ twilio_account_sid: "", twilio_auth_token: "", twilio_from_number: "", twilio_messaging_service_sid: "" });
  const [em, setEm] = useState({ email_provider: "postmark", email_from: "", email_from_name: "", postmark_token: "", smtp_host: "smtp.office365.com", smtp_port: 587, smtp_username: "", smtp_password: "" });
  const [savingTw, setSavingTw] = useState(false);
  const [savingEm, setSavingEm] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/settings/communication").then((r) => {
      setData(r.data);
      const t = r.data.twilio || {};
      setTw({
        twilio_account_sid: t.account_sid || "",
        twilio_auth_token: t.auth_token_masked || "",
        twilio_from_number: t.from_number || "",
        twilio_messaging_service_sid: t.messaging_service_sid || "",
      });
      const e = r.data.email || {};
      setEm({
        email_provider: e.provider || "postmark",
        email_from: e.email_from || "",
        email_from_name: e.email_from_name || "",
        postmark_token: e.postmark_token_masked || "",
        smtp_host: e.smtp_host || "smtp.office365.com",
        smtp_port: e.smtp_port || 587,
        smtp_username: e.smtp_username || "",
        smtp_password: e.smtp_password_masked || "",
      });
    }).catch(() => {});
  };
  useEffect(load, []);

  const saveTwilio = async () => {
    setSavingTw(true);
    try {
      const r = await api.put("/settings/communication", tw);
      setData(r.data);
      toast.success("Twilio settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingTw(false); }
  };

  const saveEmail = async () => {
    setSavingEm(true);
    try {
      const payload = {
        email_provider: em.email_provider,
        email_from: em.email_from,
        email_from_name: em.email_from_name,
      };
      if (em.email_provider === "postmark") payload.postmark_token = em.postmark_token;
      else {
        payload.smtp_host = em.smtp_host;
        payload.smtp_port = Number(em.smtp_port) || 587;
        payload.smtp_username = em.smtp_username;
        payload.smtp_password = em.smtp_password;
      }
      const r = await api.put("/settings/communication", payload);
      setData(r.data);
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingEm(false); }
  };

  const runTest = async (kind) => {
    const to = kind === "test-email" ? testEmail : testPhone;
    if (!to) { toast.error("Enter a destination first"); return; }
    setBusy(kind);
    try {
      const r = await api.post(`/settings/communication/${kind}`, { to });
      if (r.data.mock) toast.message("Sent in demo mode (credentials not live yet)");
      else toast.success("Sent — check your device");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setBusy(""); }
  };

  const twLive = data?.twilio?.live;
  const emLive = data?.email?.live;

  return (
    <>
      <Section
        title="Text & Phone (Twilio)"
        right={<LivePill live={twLive} />}
      >
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Account SID">
            <Input data-testid="tw-account-sid" value={tw.twilio_account_sid} onChange={(e) => setTw({ ...tw, twilio_account_sid: e.target.value })} placeholder="ACxxxxxxxx..." />
          </Field>
          <Field label="Auth Token" hint="Stored securely; shown masked once saved.">
            <Input data-testid="tw-auth-token" type="text" value={tw.twilio_auth_token} onChange={(e) => setTw({ ...tw, twilio_auth_token: e.target.value })} placeholder="Your Twilio auth token" />
          </Field>
          <Field label="From Number" hint="E.164 format, e.g. +13235551234. Required to send SMS & calls.">
            <Input data-testid="tw-from-number" value={tw.twilio_from_number} onChange={(e) => setTw({ ...tw, twilio_from_number: e.target.value })} placeholder="+1..." />
          </Field>
          <Field label="Messaging Service SID (optional)" hint="For A2P 10DLC campaigns. Leave blank to send from the number.">
            <Input data-testid="tw-msg-svc" value={tw.twilio_messaging_service_sid} onChange={(e) => setTw({ ...tw, twilio_messaging_service_sid: e.target.value })} placeholder="MGxxxxxxxx..." />
          </Field>
        </div>
        <div className="flex items-center gap-3 mt-5">
          <Button data-testid="tw-save-btn" onClick={saveTwilio} disabled={savingTw}>{savingTw ? "Saving..." : "Save Twilio settings"}</Button>
          {data?.twilio?.source === "env" && <span className="text-[11px] text-muted-foreground">Currently using the environment default. Save your own to override.</span>}
        </div>

        <div className="mt-6 pt-5 border-t border-border">
          <div className="text-xs font-semibold text-muted-foreground mb-3">Test delivery</div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
            <div className="flex-1">
              <Field label="Send to phone (your number)">
                <Input data-testid="test-phone-input" value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="+1..." />
              </Field>
            </div>
            <Button variant="outline" data-testid="test-sms-btn" onClick={() => runTest("test-sms")} disabled={busy === "test-sms"}>
              <MessageSquare className="h-4 w-4 mr-2" /> {busy === "test-sms" ? "Sending..." : "Test SMS"}
            </Button>
            <Button variant="outline" data-testid="test-call-btn" onClick={() => runTest("test-call")} disabled={busy === "test-call"}>
              <Phone className="h-4 w-4 mr-2" /> {busy === "test-call" ? "Calling..." : "Test Call"}
            </Button>
          </div>
        </div>
      </Section>

      <Section title="Email" right={<LivePill live={emLive} />}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Provider">
            <Select value={em.email_provider} onValueChange={(v) => setEm({ ...em, email_provider: v })}>
              <SelectTrigger data-testid="email-provider-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="postmark">Postmark</SelectItem>
                <SelectItem value="smtp">Outlook / SMTP</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="From Name">
            <Input data-testid="email-from-name" value={em.email_from_name} onChange={(e) => setEm({ ...em, email_from_name: e.target.value })} placeholder="Arqitek" />
          </Field>
          <Field label="From Email" hint={em.email_provider === "smtp" ? "Usually the same as your Outlook mailbox." : "Verified Postmark sender."}>
            <Input data-testid="email-from" value={em.email_from} onChange={(e) => setEm({ ...em, email_from: e.target.value })} placeholder="you@yourdomain.com" />
          </Field>

          {em.email_provider === "postmark" ? (
            <Field label="Postmark Server Token">
              <Input data-testid="postmark-token" value={em.postmark_token} onChange={(e) => setEm({ ...em, postmark_token: e.target.value })} placeholder="Server API token" />
            </Field>
          ) : (
            <>
              <Field label="SMTP Host">
                <Input data-testid="smtp-host" value={em.smtp_host} onChange={(e) => setEm({ ...em, smtp_host: e.target.value })} placeholder="smtp.office365.com" />
              </Field>
              <Field label="SMTP Port" hint="Outlook / Microsoft 365 uses 587 (STARTTLS).">
                <Input data-testid="smtp-port" type="number" value={em.smtp_port} onChange={(e) => setEm({ ...em, smtp_port: e.target.value })} placeholder="587" />
              </Field>
              <Field label="Username (email)">
                <Input data-testid="smtp-username" value={em.smtp_username} onChange={(e) => setEm({ ...em, smtp_username: e.target.value })} placeholder="you@outlook.com" />
              </Field>
              <Field label="Password / App Password" hint="Microsoft 365 usually requires an app password if MFA is on.">
                <Input data-testid="smtp-password" type="text" value={em.smtp_password} onChange={(e) => setEm({ ...em, smtp_password: e.target.value })} placeholder="App password" />
              </Field>
            </>
          )}
        </div>
        <div className="flex items-center gap-3 mt-5">
          <Button data-testid="email-save-btn" onClick={saveEmail} disabled={savingEm}>{savingEm ? "Saving..." : "Save email settings"}</Button>
          {data?.email?.source === "env" && <span className="text-[11px] text-muted-foreground">Currently using the environment default. Save your own to override.</span>}
        </div>

        <div className="mt-6 pt-5 border-t border-border">
          <div className="text-xs font-semibold text-muted-foreground mb-3">Test delivery</div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3">
            <div className="flex-1">
              <Field label="Send to email (your inbox)">
                <Input data-testid="test-email-input" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@yourdomain.com" />
              </Field>
            </div>
            <Button variant="outline" data-testid="test-email-btn" onClick={() => runTest("test-email")} disabled={busy === "test-email"}>
              <Send className="h-4 w-4 mr-2" /> {busy === "test-email" ? "Sending..." : "Test Email"}
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}

function RetellSettings() {
  const [data, setData] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [defaultStatus, setDefaultStatus] = useState("Mailer Received");
  const [storeRecording, setStoreRecording] = useState(true);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [events, setEvents] = useState([]);
  const [testRef, setTestRef] = useState("");
  const [testing, setTesting] = useState(false);
  const [lastTestLead, setLastTestLead] = useState(null);
  const [ledBusy, setLedBusy] = useState(false);
  const [ledResult, setLedResult] = useState("");

  const load = () => {
    api.get("/settings/retell").then((r) => {
      setData(r.data);
      setDefaultStatus(r.data.default_status || "Mailer Received");
      setStoreRecording(r.data.store_recording !== false);
    }).catch(() => {});
    api.get("/settings/retell/recent").then((r) => setRecent(r.data.items || [])).catch(() => {});
    api.get("/settings/retell/events").then((r) => setEvents(r.data.items || [])).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/retell", {
        api_key: apiKey || undefined,
        default_status: defaultStatus,
        store_recording: storeRecording,
      });
      setData(r.data);
      setApiKey("");
      toast.success("Retell settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const regenerate = async () => {
    if (!window.confirm("Generate a new webhook URL? You'll need to update it in Retell.")) return;
    try {
      const r = await api.put("/settings/retell", { regenerate_secret: true });
      setData(r.data);
      toast.success("New webhook URL generated");
    } catch { toast.error("Could not regenerate"); }
  };

  const copyUrl = () => {
    if (data?.webhook_url) {
      navigator.clipboard.writeText(data.webhook_url);
      toast.success("Webhook URL copied");
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data: r } = await api.post("/settings/retell/test", { reference_code: testRef || undefined });
      setLastTestLead(r.lead_id);
      toast.success(`Test call ${r.status} — Melissa intake attached${r.matched_by === "reference_code" ? " to that LED#" : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  const backfillLed = async () => {
    setLedBusy(true);
    setLedResult("");
    try {
      const { data: dry } = await api.post("/admin/backfill-led-numbers", {}, { params: { dry_run: true } });
      if (!dry.missing_count) {
        setLedResult("All leads already have an LED number.");
        toast.success("Nothing to fix — every lead has an LED number");
        return;
      }
      if (!window.confirm(`${dry.missing_count} lead(s) have no LED number. Assign a unique LED-#### to each now?`)) return;
      const { data: r } = await api.post("/admin/backfill-led-numbers", {}, { params: { dry_run: false } });
      setLedResult(`Assigned LED numbers to ${r.updated} lead(s).`);
      toast.success(`Assigned LED numbers to ${r.updated} lead(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    } finally { setLedBusy(false); }
  };

  const STATUS_OPTS = ["New", "Mailer Received", "Contacted", "Qualified"];

  return (
    <Section
      title="Inbound Voice Leads (Retell AI)"
      right={<LivePill live={!!data?.webhook_url} />}
    >
      <p className="text-sm text-muted-foreground mb-4">
        In Retell, paste this URL into your <span className="font-medium text-foreground">Agent &rarr; Webhook URL</span> and make sure the <span className="font-mono text-foreground">call_analyzed</span> event is enabled (Post-Call Analysis on). Each completed call then becomes a lead in the Leads module.
        <span className="block mt-2 text-amber-400">Important: the phone number's <span className="font-medium">“Inbound Call Webhook”</span> only sends <span className="font-mono">call_inbound</span> (no details) — captured data arrives on <span className="font-mono">call_analyzed</span> at the <span className="font-medium">agent</span> webhook. You can safely use this same URL in both places.</span>
        <span className="block mt-2">Tip: print each lead's <span className="font-mono text-foreground">LED-####</span> code on its postcard and have Melissa ask the caller to read it back (capture it as <span className="font-mono text-foreground">reference_code</span>). The call then attaches to that exact lead — otherwise we match by phone, or create a new lead.</span>
      </p>

      <Field label="Webhook URL" hint="Set this as the agent's webhook and enable the “call_analyzed” event.">
        <div className="flex items-center gap-2">
          <Input data-testid="retell-webhook-url" readOnly value={data?.webhook_url || ""} className="font-mono text-xs" />
          <Button type="button" variant="outline" size="icon" data-testid="retell-copy-url" onClick={copyUrl} title="Copy">
            <Copy className="h-4 w-4" />
          </Button>
          <Button type="button" variant="outline" size="icon" data-testid="retell-regen-url" onClick={regenerate} title="Generate new URL">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </Field>
      {!data?.has_public_base && (
        <p className="text-[11px] text-amber-400 mt-2">Public base URL not configured — the URL above may be relative.</p>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Field label="Retell API Key (optional)" hint="Add the webhook-badge key to verify request signatures. Blank = secret-URL only.">
          <Input data-testid="retell-api-key" type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)} placeholder={data?.has_api_key ? data.api_key_masked : "key_..."} />
        </Field>
        <Field label="New lead status" hint="What stage inbound calls land in.">
          <Select value={defaultStatus} onValueChange={setDefaultStatus}>
            <SelectTrigger data-testid="retell-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer" data-testid="retell-store-recording">
        <input type="checkbox" checked={storeRecording} onChange={(e) => setStoreRecording(e.target.checked)} className="accent-primary h-4 w-4" />
        <span>Save the call summary &amp; recording link on each lead</span>
      </label>

      <div className="flex items-center gap-3 mt-5">
        <Button data-testid="retell-save-btn" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save Retell settings"}</Button>
        <span className="text-[11px] text-muted-foreground">
          {data?.signature_required ? "Signature verification on." : "Protected by secret URL."} · {data?.captured_count || 0} captured
        </span>
      </div>

      <div className="mt-6 pt-5 border-t border-border">
        <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
          <Send className="h-3.5 w-3.5" /> Send a test call
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">Fires a sample completed call through the real pipeline so you can see the Melissa intake populate — no Retell setup or live call needed. Enter an <span className="font-mono">LED-####</span> to attach it to that exact lead, or leave blank to create a fresh one.</p>
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid="retell-test-ref" value={testRef} onChange={(e) => setTestRef(e.target.value)} placeholder="LED-2925 (optional)" className="w-full sm:w-[220px]" />
          <Button type="button" variant="outline" data-testid="retell-test-btn" onClick={sendTest} disabled={testing}>
            {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}Send test call
          </Button>
          {lastTestLead && (
            <a href={`/record/inbound_leads/${lastTestLead}`} data-testid="retell-test-open" className="text-xs text-primary hover:underline">Open the lead &rarr;</a>
          )}
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-border">
        <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Fix missing LED numbers
        </div>
        <p className="text-[11px] text-muted-foreground mb-3">Some imported leads have no <span className="font-mono">LED-####</span>. This assigns a unique one to every Lead that's missing it (safe to run anytime).</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="outline" data-testid="led-backfill-btn" onClick={backfillLed} disabled={ledBusy}>
            {ledBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}Backfill LED numbers
          </Button>
          {ledResult && <span className="text-[11px] text-muted-foreground" data-testid="led-backfill-result">{ledResult}</span>}
        </div>
      </div>

      <div className="mt-6 pt-5 border-t border-border">
        <div className="text-xs font-semibold text-muted-foreground mb-3 flex items-center justify-between">
          <span className="flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> Recent Retell events (what's actually arriving)</span>
          <button onClick={load} data-testid="retell-events-refresh" className="text-[11px] text-primary hover:underline">Refresh</button>
        </div>
        {events.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="retell-no-events">No webhook calls received yet. When Retell hits the URL, every event shows here.</p>
        ) : (
          <div className="divide-y divide-border" data-testid="retell-events-list">
            {events.map((e) => {
              const captured = e.event === "call_analyzed" || (e.event === "call_ended" && e.has_analysis);
              return (
                <div key={e.id} className="flex items-center justify-between py-2 text-sm" data-testid={`retell-event-${e.event}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${captured ? "bg-emerald-500/15 text-emerald-400" : e.event === "call_inbound" ? "bg-amber-500/15 text-amber-400" : "bg-secondary text-muted-foreground"}`}>{e.event || "unknown"}</span>
                    <span className="text-xs text-muted-foreground truncate">{e.from_number || "—"}{captured ? " · captured" : e.event === "call_inbound" ? " · no data (inbound webhook)" : " · ignored"}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">{e.received_at ? new Date(e.received_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-6 pt-5 border-t border-border">
        <div className="text-xs font-semibold text-muted-foreground mb-3 flex items-center gap-1.5">
          <PhoneIncoming className="h-3.5 w-3.5" /> Recent inbound calls
        </div>
        {recent.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="retell-no-calls">No calls captured yet. They'll appear here once Retell posts a completed call.</p>
        ) : (
          <div className="divide-y divide-border" data-testid="retell-recent-list">
            {recent.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <div className="font-medium">{r.owner_name || [r.first_name, r.last_name].filter(Boolean).join(" ") || "Unknown caller"}</div>
                  <div className="text-xs text-muted-foreground">{[r.mobile_phone, r.address].filter(Boolean).join(" · ") || "No details"}</div>
                </div>
                <div className="text-right">
                  <StatusBadge value={r.lead_status} />
                  {r.retell_campaign_id && <div className="text-[10px] text-muted-foreground mt-0.5">{r.retell_campaign_id}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

export default function Settings() {
  const { user, logout, isAdmin } = useAuth();

  return (
    <div className="p-6 lg:p-8 max-w-[900px] mx-auto space-y-6">
      <PageHeader title="Settings" subtitle="Manage your profile and preferences" />

      <Section title="Profile">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-lg font-semibold">{initials(user?.name || "U")}</div>
          <div><div className="font-medium">{user?.name}</div><div className="text-sm text-muted-foreground">{user?.email}</div><div className="mt-1"><StatusBadge value={user?.role} /></div></div>
        </div>
      </Section>

      <Section title="Company">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><div className="text-xs text-muted-foreground">Platform</div><div className="font-medium">Arqitek</div></div>
          <div><div className="text-xs text-muted-foreground">Primary Product</div><div className="font-medium">Arq+</div></div>
          <div><div className="text-xs text-muted-foreground">Appearance</div><div className="font-medium">Dark (fixed)</div></div>
          <div><div className="text-xs text-muted-foreground">Users</div><div className="font-medium">Team access enabled</div></div>
        </div>
      </Section>

      {isAdmin && <CommunicationsSettings />}
      {isAdmin && <RetellSettings />}

      <Section title="Compliance Defaults">
        <p className="text-sm text-muted-foreground">Email, SMS, and phone consent are tracked per lead. Automations and AI agents respect all Do-Not-Contact preferences and opt-out requests.</p>
      </Section>

      <Section title="Account">
        <Button variant="outline" className="text-destructive" onClick={logout} data-testid="settings-logout">Sign out</Button>
      </Section>
    </div>
  );
}
